import { useTranslation } from 'react-i18next';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';

import { Button } from '@speeki/global-ui-components';
import { useQueryInfinitePaginationV2 } from '@speeki/react-hooks';

import { ArrElement } from '@@types/CommonTypes';

import { EmptyList } from '@components/atoms/EmptyList/EmptyList';

import {
    ReportCasesQuery,
    ReportCasesQueryVariables,
} from '@graphql/generated/graphql';

import { getPath } from '@services/RouteService/paths';

import { useReactiveVar } from '@apollo/client';
import {
    resetReporterDetails,
    translationsStatusVar,
} from '@boot/reactiveVariables';
import { CasesListItem } from '@domains/cases/molecules/CasesListItem/CasesListItem';
import { SkeletonCaseListItem } from '@domains/cases/molecules/SkeletonCaseListItem/SkeletonCaseListItem';
import IconReportsEmpty from '@shared/svgs/IconReportsEmpty.svg';

import {
    ButtonWrapper,
    DateCell,
    MessageCell,
    ReportCell,
    ReportListBody,
    ReportListHeader,
    ReportListNoItems,
    ReportListWrapper,
    StatusCell,
} from './CasesList.styled';

interface QueryResult extends ReturnType<
    typeof useQueryInfinitePaginationV2<
        ArrElement<ReportCasesQuery['list']['edges']>['node'],
        ReportCasesQueryVariables
    >
> {
    called: boolean;
    error: boolean;
    loading: boolean;
}

interface CaseListProps {
    queryResult: QueryResult;
    searchPhrase: string;
}

export const CasesList = ({
    queryResult: {
        called,
        error,
        handleFetchAfter,
        hasNextPage,
        isEmptyList,
        loading,
        nodes,
    },
    searchPhrase,
}: CaseListProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const loadingTranslate =
        useReactiveVar(translationsStatusVar) === 'loading';

    const firstLoading = loading && isEmptyList;
    const nextLoading = loading && !isEmptyList;
    const initialEmptyCases =
        !searchPhrase && isEmptyList && !loading && called;

    const [sentryRef] = useInfiniteScroll({
        disabled: !!error,
        hasNextPage,
        loading: firstLoading || nextLoading,
        onLoadMore: handleFetchAfter,
        rootMargin: '0px 0px 50px 0px',
    });

    const cellItem = (key: string) => (
        <>
            {loadingTranslate && <Skeleton height={14} width={'100%'} />}
            {!loadingTranslate && t(key)}
        </>
    );

    if (initialEmptyCases) {
        return (
            <>
                <EmptyList
                    description={t('NO_ACTIVE_REPORTS_INFO')}
                    icon={IconReportsEmpty}
                    title={t('NOTHINGS_HERE_TEXT')}
                />
                <ButtonWrapper>
                    <Button
                        onClick={() => {
                            navigate(getPath({ path: '/new-conversation' }));
                            resetReporterDetails();
                        }}
                        testId="1jNzP4h"
                        variant={'primary'}
                    >
                        {t('CREATE_NEW_REPORT_TEXT')}
                    </Button>
                </ButtonWrapper>
            </>
        );
    }

    return (
        <ReportListWrapper>
            {called && (
                <ReportListHeader>
                    <ReportCell>{cellItem('REPORT_ID')}</ReportCell>
                    <StatusCell>{cellItem('STATUS')}</StatusCell>
                    <MessageCell>{cellItem('MESSAGE')}</MessageCell>
                    <DateCell>{cellItem('LAST_MESSAGE_DATE')}</DateCell>
                </ReportListHeader>
            )}

            {firstLoading && <SkeletonCaseListItem />}
            {!firstLoading && (
                <>
                    <ReportListBody>
                        {nodes.map((item, index) => (
                            <CasesListItem
                                chatRoomSecret={item.chatRoomSecret ?? ''}
                                id={item.uuid}
                                isStatusChanged={item.isStatusChanged}
                                key={item.uuid}
                                reportId={item.caseId}
                                status={item.status}
                                testId={`5uw9uGr-${index}`}
                            />
                        ))}
                        {nextLoading && (
                            <SkeletonCaseListItem numberOfElements={1} />
                        )}
                        {hasNextPage && <div ref={sentryRef} />}
                    </ReportListBody>
                </>
            )}

            {searchPhrase && isEmptyList && !firstLoading && !nextLoading && (
                <ReportListBody>
                    <ReportListNoItems>{t('NO_REPORTS')}</ReportListNoItems>
                </ReportListBody>
            )}
        </ReportListWrapper>
    );
};
